import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, Tag, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { Input } from "../components/ui/input";
import { fetchShippingConfig, getShippingForCountry, getCountryListFromConfig } from "../lib/shipping";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { t } = useLanguage();
  const { items, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  // Discount code
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  // Shipping country
  const [shippingCountry, setShippingCountry] = useState(() => {
    try { return localStorage.getItem("mona_shipping_country") || ""; } catch { return ""; }
  });
  const [shippingConfig, setShippingConfig] = useState(null);
  useEffect(() => {
    fetchShippingConfig().then(setShippingConfig).catch((e) => console.error(e));
  }, []);
  useEffect(() => {
    if (shippingCountry) {
      try { localStorage.setItem("mona_shipping_country", shippingCountry); } catch {
        // ignore quota errors
      }
    }
  }, [shippingCountry]);

  const getItemPrice = (item) => {
    const p = item.product;
    return p.discount > 0 ? p.price * (1 - p.discount / 100) : p.price;
  };

  const discountAmount = discountApplied ? discountApplied.discount_amount : 0;
  const subtotalAfterDiscount = Math.max(0, totalPrice - discountAmount);
  const shippingInfo = shippingCountry ? getShippingForCountry(shippingConfig, shippingCountry, subtotalAfterDiscount) : null;
  const shippingCost = shippingInfo ? shippingInfo.cost : 0;
  const finalTotal = subtotalAfterDiscount + shippingCost;
  const countryList = getCountryListFromConfig(shippingConfig);
  const freeShippingThreshold = shippingConfig?.free_shipping_threshold ?? 1000;

  const handleValidateCode = async () => {
    if (!discountCode.trim()) return;
    setValidatingCode(true);
    try {
      const res = await axios.post(`${API}/discount/validate`, {
        code: discountCode.trim(),
        subtotal: totalPrice,
      });
      setDiscountApplied(res.data);
      toast.success(`Code "${res.data.code}" applied!`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid code";
      toast.error(msg);
      setDiscountApplied(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleCheckout = async () => {
    if (!shippingCountry) {
      toast.error(t("cart.select_country"));
      return;
    }
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        discount_code: discountApplied?.code || null,
        shipping_country: shippingCountry,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      toast.error(t("common.error"));
    } finally {
      setCheckingOut(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center" data-testid="cart-page">
        <ShoppingBag size={40} strokeWidth={1} className="mx-auto mb-6 text-[#E6E2DD]" />
        <h1 className="text-2xl md:text-3xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t("cart.empty_title")}
        </h1>
        <p className="text-sm text-[#9CA3AF] tracking-wide mb-8">{t("cart.empty_subtitle")}</p>
        <Link to="/shop" className="btn-luxury inline-block" data-testid="shop-now-btn">{t("cart.shop_now")}</Link>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-24 py-12 md:py-20" data-testid="cart-page">
      <h1 className="text-3xl md:text-4xl mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
        {t("cart.title")}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Left: Cart Items */}
        <div className="lg:col-span-2">
          <div className="space-y-6" data-testid="cart-items">
            {items.map((item) => (
              <div key={item.product.id} className="flex gap-4 md:gap-6 border-b border-[#E6E2DD]/50 pb-6" data-testid={`cart-item-${item.product.id}`}>
                <Link to={`/product/${item.product.id}`} className="w-20 h-24 md:w-24 md:h-32 bg-[#F4F0EB] flex-shrink-0">
                  <img src={item.product.images?.[0] || ""} alt={item.product.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">{item.product.brand}</p>
                    <Link to={`/product/${item.product.id}`}>
                      <h3 className="text-sm tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>{item.product.name}</h3>
                    </Link>
                    <div className="flex items-center gap-2 mt-1">
                      {item.product.discount > 0 && (
                        <p className="text-xs tracking-wider line-through text-[#9CA3AF]">
                          &euro;{item.product.price?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                        </p>
                      )}
                      <p className={`text-xs tracking-wider ${item.product.discount > 0 ? "text-[#8B2020] font-semibold" : ""}`}>
                        &euro;{getItemPrice(item)?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center border border-[#E6E2DD] hover:border-[#1A1A1A] transition-colors" data-testid={`qty-decrease-${item.product.id}`}>
                        <Minus size={12} strokeWidth={1.5} />
                      </button>
                      <span className="w-8 text-center text-xs tracking-wider" data-testid={`qty-value-${item.product.id}`}>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} disabled={item.quantity >= (item.product.stock_quantity || 999)} className="w-8 h-8 flex items-center justify-center border border-[#E6E2DD] hover:border-[#1A1A1A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`qty-increase-${item.product.id}`}>
                        <Plus size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs tracking-wider">
                        &euro;{(getItemPrice(item) * item.quantity).toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                      </span>
                      <button onClick={() => removeItem(item.product.id)} className="text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors" data-testid={`remove-item-${item.product.id}`}>
                        <X size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Summary */}
        <div>
          <div className="bg-[#F4F0EB]/50 p-8" data-testid="order-summary">
            <h2 className="text-sm uppercase tracking-[0.15em] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("cart.summary")}
            </h2>
            <div className="space-y-4 mb-6">
              {items.map((item) => (
                <div key={item.product.id} className="flex justify-between text-xs tracking-wide">
                  <span className="text-[#1A1A1A]/70">{item.product.name} x{item.quantity}</span>
                  <span>&euro;{(getItemPrice(item) * item.quantity).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>

            {/* Discount Code */}
            <div className="mb-6 pb-6 border-b border-[#E6E2DD]">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder={t("checkout.discount_code")}
                    className="border-[#E6E2DD] bg-transparent pl-9 text-xs uppercase tracking-wider"
                    data-testid="discount-code-input"
                  />
                </div>
                <button
                  onClick={handleValidateCode}
                  disabled={validatingCode || !discountCode.trim()}
                  className="btn-luxury-outline text-[10px] uppercase tracking-wider px-4 disabled:opacity-30"
                  data-testid="apply-discount-btn"
                >
                  {validatingCode ? <Loader2 size={14} className="animate-spin" /> : t("checkout.apply")}
                </button>
              </div>
              {discountApplied && (
                <div className="flex justify-between mt-3 text-xs tracking-wide text-green-700" data-testid="discount-applied">
                  <span>{discountApplied.code} ({discountApplied.type === "percentage" ? `${discountApplied.value}%` : `€${discountApplied.value}`})</span>
                  <span>-&euro;{discountApplied.discount_amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>

            <div className="flex justify-between text-sm tracking-wide mb-2">
              <span>{t("cart.subtotal")}</span>
              <span>&euro;{totalPrice.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</span>
            </div>
            {discountApplied && (
              <div className="flex justify-between text-sm tracking-wide mb-2 text-green-700">
                <span>{t("checkout.discount")}</span>
                <span>-&euro;{discountAmount.toLocaleString("de-DE", { minimumFractionDigits: 2 })}</span>
              </div>
            )}

            {/* Shipping country selector */}
            <div className="mt-4 pt-4 border-t border-[#E6E2DD]">
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] block mb-2" htmlFor="shipping-country">
                {t("cart.ship_to")}
              </label>
              <div className="relative">
                <select
                  id="shipping-country"
                  value={shippingCountry}
                  onChange={(e) => setShippingCountry(e.target.value)}
                  className="w-full appearance-none bg-transparent border border-[#E6E2DD] hover:border-[#1A1A1A] focus:border-[#1A1A1A] focus:outline-none px-3 py-2 text-xs tracking-wide pr-8 transition-colors cursor-pointer"
                  data-testid="shipping-country-select"
                >
                  <option value="">{t("cart.select_country")}</option>
                  {countryList.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none text-[#9CA3AF]" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 4.5L6 7.5L9 4.5" />
                </svg>
              </div>
              {shippingInfo && (
                <div className="flex justify-between mt-3 text-xs tracking-wide" data-testid="shipping-line">
                  <span className="text-[#1A1A1A]/70">
                    {t("cart.shipping")}
                    <span className="text-[#9CA3AF] ml-1">({shippingInfo.etaMin}–{shippingInfo.etaMax} {t("cart.business_days")})</span>
                  </span>
                  <span className={shippingCost === 0 ? "text-green-700" : ""}>
                    {shippingCost === 0 ? t("cart.free") : `€${shippingCost.toLocaleString("de-DE", { minimumFractionDigits: 2 })}`}
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-[#E6E2DD] pt-4 mt-4">
              <div className="flex justify-between text-sm tracking-wide">
                <span className="font-bold">{t("cart.total")}</span>
                <span className="font-bold" data-testid="cart-total">
                  &euro;{finalTotal.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-[10px] tracking-wide text-[#9CA3AF] mt-2">incl. BTW 21%</p>
              {subtotalAfterDiscount >= freeShippingThreshold ? (
                <p className="text-[10px] tracking-wide text-green-700 mt-1">{t("cart.free_shipping")}</p>
              ) : (
                <p className="text-[10px] tracking-wide text-[#9CA3AF] mt-1">{t("cart.free_shipping_from")} &euro;{freeShippingThreshold.toLocaleString("de-DE")}</p>
              )}
            </div>

            <button
              onClick={handleCheckout}
              disabled={checkingOut || !shippingCountry}
              className="btn-luxury w-full mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="checkout-btn"
            >
              {checkingOut ? <Loader2 size={16} className="animate-spin mx-auto" /> : t("checkout.proceed")}
            </button>
            <Link to="/shop" className="block text-center mt-4 text-xs uppercase tracking-widest text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors" data-testid="continue-shopping-link">
              {t("cart.continue")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
